import { isNumber } from 'lodash';
import { PropsWithChildren } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import RemoveIcon from '@mui/icons-material/Remove';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useHostingAccount, useSitesInfo } from '@newfold/huapi-js';

import Tooltip from '~/components/CommonTooltip';
import useAccount from '~/hooks/useAccount';
import { validateWorkers } from '~/utils/cloud/validations';
import { isSiteIdAvailable } from '~/utils/site/helpers';

import { blockInvalidNumberChar } from '../utils/helpers';

interface WorkersTypes {
  showLabel?: boolean;
  defaultValue: number | undefined | null;
  hideControls?: boolean;
  siteId?: number | undefined;
  isWorkersDisabled?: boolean;
  hideHelperText?: boolean;
  isStagingSite?: boolean;
}

const Workers = ({
  showLabel = false,
  hideHelperText = false,
  defaultValue,
  hideControls = false,
  siteId = undefined,
  isWorkersDisabled = false,
  isStagingSite = false,
  ...props
}: PropsWithChildren<WorkersTypes>) => {
  const { t } = useTranslation('addSites');
  const { t: tCloud } = useTranslation('cloud');
  const { register, watch, setValue } = useFormContext();
  const MAX_ALLOWED_PHP_WORKERS = 10;

  const workersRegisteredId = isNumber(siteId)
    ? `${siteId}-workers`
    : 'workers';
  const value = watch(workersRegisteredId, defaultValue);

  const { id: hostingId } = useAccount();
  const { data: hostingAccountData, isPending } = useHostingAccount(hostingId, {
    query: {
      enabled: !!hostingId,
      select: ({ data }) => data,
    },
  });

  const { data: siteData } = useSitesInfo(Number(siteId), {
    query: {
      enabled: isSiteIdAvailable(siteId),
    },
  });

  const workersAvailable = isStagingSite
    ? hostingAccountData?.resources?.staging?.workers?.available
    : hostingAccountData?.resources?.workers?.available;

  const minAllowed = 2;
  const siteWorkers = (!!siteId && siteData?.data?.resources?.workers) || 0;

  const calculateMaxAllowed = (
    workers: number,
    additionalWorkers: number = 0,
  ) => Math.min(workers + additionalWorkers, MAX_ALLOWED_PHP_WORKERS);

  const maxAllowed = isNumber(workersAvailable)
    ? calculateMaxAllowed(workersAvailable, siteId ? siteWorkers : 0)
    : MAX_ALLOWED_PHP_WORKERS;

  const { isValid, errorMessage } = validateWorkers({
    value,
    min: minAllowed,
    max: maxAllowed,
    ...(!!siteId && {
      available: workersAvailable,
    }),
    ...(!!siteId && { allocated: siteData?.data?.resources?.workers }),
    t: tCloud,
  });

  const decrementWorkerValue = () =>
    setValue(workersRegisteredId, Number(value) - 1);

  const incrementWorkerValue = () =>
    setValue(workersRegisteredId, Number(value) + 1);

  const helperTextStyles = {
    maxWidth: !showLabel ? '150px' : '250px',
    ...(showLabel && { px: '40px' }),
  };

  return (
    <Stack>
      <Stack direction="row" spacing={1} alignItems={'center'}>
        {isPending ? (
          <Skeleton width={260} height={56} />
        ) : (
          <>
            {!hideControls && (
              // @ts-ignore
              <IconButton
                styletype="counter"
                onClick={decrementWorkerValue}
                disabled={isWorkersDisabled || value <= minAllowed}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            )}
            <TextField
              id={`${siteId}`}
              type="number"
              {...(showLabel && {
                label: (
                  <Stack direction="row" spacing={1} alignItems={'center'}>
                    <Typography>{t('siteTitle.phpWorkersLabel')}</Typography>
                    <Tooltip
                      title={
                        isStagingSite
                          ? t('addSiteOptions.cloud.staging.phpWorkersTooltip')
                          : t('siteTitle.phpWorkersTooltip')
                      }
                      arrow
                      placement="top"
                      data-testid="workers-tooltip"
                    >
                      {/* @ts-ignore */}
                      <InfoIcon color="action" />
                    </Tooltip>
                  </Stack>
                ),
              })}
              {...(!showLabel && { sx: { width: '70px' } })}
              data-testid={`${workersRegisteredId}-input`}
              variant="outlined"
              value={value}
              error={!isValid}
              disabled={hideControls || isWorkersDisabled}
              onKeyDown={blockInvalidNumberChar}
              {...register(workersRegisteredId)}
              {...props}
            />

            {!hideControls && (
              // @ts-ignore
              <IconButton
                styletype="counter"
                onClick={incrementWorkerValue}
                disabled={isWorkersDisabled || value >= maxAllowed}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            )}
          </>
        )}
      </Stack>
      {!hideHelperText && !isValid && (
        <FormHelperText
          error
          {...((!hideControls || !showLabel) && { sx: helperTextStyles })}
        >
          {errorMessage}
        </FormHelperText>
      )}
    </Stack>
  );
};

export default Workers;
